import * as DV from "@impero-com/common/utils/dataView";
import { formatFileNameForExport } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import * as XLSXUtils from "@impero-com/common/utils/xlsx";
import { Button } from "@impero-com/ui";
import saveAs from "save-as";
import * as XLSX from "xlsx";

import { EXPORT_CONFIG, useControlTemplate } from "../hooks/useControlTemplate";

interface ControlTemplateExporterProps {
    onExport: () => void;
    includeExampleRow?: boolean;
}

export function ControlTemplateExporter({
    onExport,
    includeExampleRow = false,
}: ControlTemplateExporterProps) {
    const { t } = useTranslation();
    const {
        getBaseColumns,
        getTagColumns,
        getTaskColumns,
        getFirstReviewerTaskColumns,
        getSecondReviewerTaskColumns,
        getExportMetadata,
        getInstructionsContent,
    } = useControlTemplate();

    const handleDownload = () => {
        const allColumns = [
            ...getBaseColumns(),
            ...getTagColumns(),
            ...getTaskColumns(),
            ...getFirstReviewerTaskColumns(),
            ...getSecondReviewerTaskColumns(),
        ];

        const dataSourceItems = includeExampleRow ? [{ id: 1 }] : [];
        const dataSource = new DV.ArrayDataSource(dataSourceItems, (item) =>
            item.id.toString()
        );

        const dataView: DV.DataView<{ id: number }> = {
            dataSource,
            extractorGroups: [
                {
                    id: "template",
                    label: "Template",
                    extractors: allColumns.map((col) => ({
                        id: col.label,
                        label: col.label,
                        getValue: () => {
                            if (!includeExampleRow) {
                                return "";
                            }

                            if (col.type === "number" && col.example) {
                                return parseInt(col.example, 10);
                            }

                            return col.example;
                        },
                        compare: () => 0,
                        asString: () =>
                            includeExampleRow
                                ? (col.example?.toString() ?? "")
                                : "",
                    })),
                },
            ],
            sorters: [],
            defaultCompareItems: () => 0,
        };

        // Main template sheet
        const templateExportableArray = DV.exportToTable(
            dataView,
            () => true,
            allColumns.map((col) => ({
                label: col.label,
                getValue: () => (includeExampleRow ? col.example : ""),
            }))
        );
        const templateWorksheet = XLSXUtils.tableToWorksheet(
            templateExportableArray,
            undefined,
            getExportMetadata()
        );

        // Instructions sheet
        const instructionsContent = getInstructionsContent();
        const instructionsWorksheet =
            XLSX.utils.aoa_to_sheet(instructionsContent);

        const workbook = {
            SheetNames: [
                EXPORT_CONFIG.MAIN_SHEET_NAME,
                EXPORT_CONFIG.INSTRUCTIONS_SHEET_NAME,
            ],
            Sheets: {
                [EXPORT_CONFIG.MAIN_SHEET_NAME]: templateWorksheet,
                [EXPORT_CONFIG.INSTRUCTIONS_SHEET_NAME]: instructionsWorksheet,
            },
        };

        // Export the workbook
        const data = XLSXUtils.workbookToBinary(workbook);
        const blob = new Blob([data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(
            blob,
            formatFileNameForExport(
                EXPORT_CONFIG.FILENAME,
                EXPORT_CONFIG.FILE_FORMAT
            )
        );

        onExport();
    };

    return (
        <Button onClick={handleDownload}>
            {t("ControlsBulkImport_ExportStep_TemplateExporter_ExportButton")}
        </Button>
    );
}
